import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AxiosResultDefaultError } from '../../../../api/request';
import { createFetchContext } from '../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { TranslationsKeys } from '../../../../locales/translations';
import { getHomologationRequests } from '../api';
import { HomologationListFilter, HomologationListResponse } from '../types';

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  HomologationListFilter,
  HomologationListResponse,
  AxiosResultDefaultError
>();

export function useHomologationDataLoader() {
  return useFetch();
}

/**
 * Wrapper that provides the FetchProvider context.
 * Children (search bar + table) are always rendered,
 * so filters stay visible during loading.
 */
export default function HomologationDataLoader({
  children,
}: {
  children: ReactElement | ReactElement[];
}) {
  const request = useCallback(async (query: HomologationListFilter) => {
    return getHomologationRequests({
      ...query,
      page: query.page - 1,
    });
  }, []);

  return (
    <FetchProvider
      request={request}
      defaultQuery={{ page: 1, items_per_page: 10 }}
      fetchImmediately
    >
      {children}
    </FetchProvider>
  );
}

/**
 * Wraps the table content with loading/error states.
 * Only the table area shows loading, not the filters.
 */
export function HomologationTableContainer({
  children,
}: {
  children: ReactElement;
}) {
  const { t } = useTranslation(TranslationsKeys.HOMOLOGATION);

  return (
    <FetchConsumer>
      {({ loading, data, error, refresh }) => {
        if (loading) {
          return <Loading insideCard />;
        } else if (error) {
          return (
            <DisplayError
              insideCard
              title={t('error.title')}
              textBody={t('error.body')}
              retryAction={refresh}
            />
          );
        } else if (data) {
          return <>{children}</>;
        }
        return null;
      }}
    </FetchConsumer>
  );
}
